// src/autodesk_usage/hooks/useCorporateProductUsageByMonth.ts
// All comments in English only
import { useQuery } from "@tanstack/react-query";
import {
  getCorporateProductUsageByMonth,
  type CorporateProductUsageSummary,
} from "../api/corporate";

export interface UseCorporateProductUsageByMonthResult {
  data?: CorporateProductUsageSummary[];
  isLoading: boolean;
  error: unknown;
}

function normalizeCorporateNames(value?: string | string[] | null) {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return Array.from(
    new Set(list.map((item) => item.trim()).filter(Boolean))
  ).sort();
}

function normalizeCorporateProductUsagePayload(
  payload: unknown
): CorporateProductUsageSummary[] {
  if (Array.isArray(payload)) {
    return payload as CorporateProductUsageSummary[];
  }
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as any).items)
  ) {
    return (payload as any).items as CorporateProductUsageSummary[];
  }
  return [];
}

export function useCorporateProductUsageByMonth(params?: {
  corporateName?: string | string[];
  month?: string;
  startMonth?: string;
  endMonth?: string;
}): UseCorporateProductUsageByMonthResult {
  const corporateNames = normalizeCorporateNames(params?.corporateName);
  const corporateKey = corporateNames.join("|");
  const query = useQuery<CorporateProductUsageSummary[]>({
    queryKey: [
      "corporate-product-usage",
      corporateKey,
      params?.month,
      params?.startMonth,
      params?.endMonth,
    ],
    queryFn: async () => {
      if (!corporateNames.length) return [];
      const res = await getCorporateProductUsageByMonth(
        corporateNames,
        params.month,
        params.startMonth,
        params.endMonth
      );
      return normalizeCorporateProductUsagePayload(res.data);
    },
    enabled: corporateNames.length > 0,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}
